import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { updateMessageOptions } from "../../../store/Slices/MessageOptionsSlice";
import { fetchChatMessages } from "../../../utils/fetchChatMessages";
import {
  appendNewerMessages,
  prependOlderMessages,
} from "../../../store/Slices/MessageListSlice";
import {
  captureMessageListScroll,
  clearPendingMessageListScroll,
  getNewestMessageId,
  getOldestMessageId,
} from "../../../utils/messageListWindow";
import { store } from "../../../store/store";

/** Prefetch only when close to an edge — feels continuous, not a teleport */
const PREFETCH_DISTANCE_PX = 700;
const LOAD_COOLDOWN_MS = 800;
/** Leave the edge before the next pack can auto-fire */
const REARM_DISTANCE_PX = 500;

/**
 * Older packs: approach top edge → before_message_id.
 * Newer packs (after pin/jump window): approach tip → after_message_id.
 */
export default function LoadMoreMessages() {
  const dispatch = useAppDispatch();
  const inFlightRef = useRef(false);
  const armedRef = useRef(false);
  const olderEdgeReadyRef = useRef(true);
  const newerEdgeReadyRef = useRef(true);
  const cooldownUntilRef = useRef(0);

  const oldestIdRef = useRef<number | null>(null);
  const newestIdRef = useRef<number | null>(null);
  const chatIdRef = useRef(0);
  const hasMoreOlderRef = useRef(false);
  const hasMoreNewerRef = useRef(false);
  const userIdRef = useRef<number | undefined>(undefined);

  const messageListDetails = useAppSelector((state) => state.MessageOptions);
  const messageList = useAppSelector((state) => state.MessageList);
  const currentConversation = useAppSelector(
    (state) => state.CurrentConversation,
  );

  oldestIdRef.current = getOldestMessageId(messageList.message_list.Records);
  newestIdRef.current = getNewestMessageId(messageList.message_list.Records);
  chatIdRef.current = currentConversation.chat_id;
  userIdRef.current = currentConversation.user_id;
  hasMoreOlderRef.current = messageListDetails.has_more_older;
  hasMoreNewerRef.current = messageListDetails.has_more_newer;

  useEffect(() => {
    const t = window.setTimeout(() => {
      armedRef.current = true;
      olderEdgeReadyRef.current = true;
      newerEdgeReadyRef.current = true;
      cooldownUntilRef.current = 0;
    }, 400);
    return () => {
      window.clearTimeout(t);
      armedRef.current = false;
      inFlightRef.current = false;
      olderEdgeReadyRef.current = true;
      newerEdgeReadyRef.current = true;
      clearPendingMessageListScroll();
    };
  }, [currentConversation.chat_id]);

  function messageEl(
    container: HTMLElement,
    id: number,
  ): HTMLElement | null {
    return (
      container.querySelector<HTMLElement>(`[data-message-id="${id}"]`) ||
      document.getElementById(String(id))
    );
  }

  /** Positive when oldest bubble is above the viewport (need to scroll up). */
  function distanceToOldest(container: HTMLElement): number | null {
    const oldestId = oldestIdRef.current;
    if (!oldestId) return null;
    const oldestEl = messageEl(container, oldestId);
    if (!oldestEl) return null;
    const cRect = container.getBoundingClientRect();
    const oRect = oldestEl.getBoundingClientRect();
    return cRect.top - oRect.bottom;
  }

  /** Positive when newest bubble is below the viewport (need to scroll down). */
  function distanceToNewest(container: HTMLElement): number | null {
    const newestId = newestIdRef.current;
    if (!newestId) return null;
    const newestEl = messageEl(container, newestId);
    if (!newestEl) return null;
    const cRect = container.getBoundingClientRect();
    const nRect = newestEl.getBoundingClientRect();
    return nRect.top - cRect.bottom;
  }

  async function loadOlder() {
    if (!armedRef.current || !olderEdgeReadyRef.current) return;
    if (inFlightRef.current || !hasMoreOlderRef.current) return;
    if (Date.now() < cooldownUntilRef.current) return;

    const chatId = chatIdRef.current;
    const oldestId = oldestIdRef.current;
    const el = document.getElementById("messageListDiv");
    if (!chatId || !oldestId || !el) return;

    olderEdgeReadyRef.current = false;
    inFlightRef.current = true;
    dispatch(
      updateMessageOptions({
        isMoreMessageLoading: true,
        messageListAtTop: false,
      }),
    );

    try {
      const res = await fetchChatMessages({
        chat_id: chatId,
        user_id: userIdRef.current,
        before_message_id: oldestId,
        pageSize: 25,
        pinned: false,
      });

      if (chatIdRef.current !== chatId) return;

      const records = res?.message_list?.Records || [];
      const pagination = res?.message_list?.Pagination;
      const recordChatId = records.find(
        (m) => m?.message_type !== "date" && m?.chat_id != null,
      )?.chat_id;
      if (recordChatId != null && Number(recordChatId) !== Number(chatId)) {
        return;
      }

      const beforeRecords =
        store.getState().MessageList.message_list.Records;
      const oldestBefore = getOldestMessageId(beforeRecords);
      const newestBefore = getNewestMessageId(beforeRecords);
      captureMessageListScroll(el);
      dispatch(prependOlderMessages(records));
      const afterRecords =
        store.getState().MessageList.message_list.Records;
      const oldestAfter = getOldestMessageId(afterRecords);
      const newestAfter = getNewestMessageId(afterRecords);
      // No-op prepend: unfreeze now. Any real window change (incl. tip trim
      // with unchanged length) must wait for MessageBody useLayoutEffect —
      // restoring here runs on the pre-commit DOM and clears the anchor.
      const windowUnchanged =
        oldestBefore === oldestAfter && newestBefore === newestAfter;
      if (windowUnchanged) {
        clearPendingMessageListScroll();
      }

      const hasMore =
        pagination?.has_more_older ?? records.length >= 25;
      const tipTrimmed =
        newestBefore != null &&
        newestAfter != null &&
        newestBefore !== newestAfter;
      const viewingPast = tipTrimmed || hasMoreNewerRef.current;

      dispatch(
        updateMessageOptions({
          historyMode: viewingPast ? "window" : "page",
          has_more_older: hasMore,
          has_more_newer: viewingPast,
          isMoreMessageLoading: false,
        }),
      );

      cooldownUntilRef.current = Date.now() + LOAD_COOLDOWN_MS;
    } catch {
      clearPendingMessageListScroll();
      dispatch(
        updateMessageOptions({
          isMoreMessageLoading: false,
          has_more_older: false,
        }),
      );
      olderEdgeReadyRef.current = true;
    } finally {
      inFlightRef.current = false;
    }
  }

  async function loadNewer() {
    if (!armedRef.current || !newerEdgeReadyRef.current) return;
    if (inFlightRef.current || !hasMoreNewerRef.current) return;
    if (Date.now() < cooldownUntilRef.current) return;

    const chatId = chatIdRef.current;
    const newestId = newestIdRef.current;
    const el = document.getElementById("messageListDiv");
    if (!chatId || !newestId || !el) return;

    newerEdgeReadyRef.current = false;
    inFlightRef.current = true;
    dispatch(
      updateMessageOptions({
        isMoreMessageLoading: true,
      }),
    );

    try {
      const res = await fetchChatMessages({
        chat_id: chatId,
        user_id: userIdRef.current,
        after_message_id: newestId,
        pageSize: 25,
        pinned: false,
      });

      if (chatIdRef.current !== chatId) return;

      const records = res?.message_list?.Records || [];
      const pagination = res?.message_list?.Pagination;
      const recordChatId = records.find(
        (m) => m?.message_type !== "date" && m?.chat_id != null,
      )?.chat_id;
      if (recordChatId != null && Number(recordChatId) !== Number(chatId)) {
        return;
      }

      // Keep the on-screen bubble fixed while newer packs land below
      const beforeRecords =
        store.getState().MessageList.message_list.Records;
      const oldestBefore = getOldestMessageId(beforeRecords);
      const newestBefore = getNewestMessageId(beforeRecords);
      captureMessageListScroll(el);
      dispatch(appendNewerMessages(records));
      const afterRecords =
        store.getState().MessageList.message_list.Records;
      const oldestAfter = getOldestMessageId(afterRecords);
      const newestAfter = getNewestMessageId(afterRecords);
      const windowUnchanged =
        oldestBefore === oldestAfter && newestBefore === newestAfter;
      if (windowUnchanged) {
        clearPendingMessageListScroll();
      }

      // Trust server has_more_newer (honest relative to returned window).
      const hasMoreNewer =
        typeof pagination?.has_more_newer === "boolean"
          ? pagination.has_more_newer
          : records.length >= 25;
      const oldestTrimmed =
        oldestBefore != null &&
        oldestAfter != null &&
        oldestBefore !== oldestAfter;
      const hasMoreOlder =
        oldestTrimmed ||
        hasMoreOlderRef.current ||
        !!pagination?.has_more_older;

      dispatch(
        updateMessageOptions({
          historyMode: hasMoreNewer ? "window" : "page",
          has_more_newer: hasMoreNewer,
          has_more_older: hasMoreOlder,
          isMoreMessageLoading: false,
        }),
      );

      cooldownUntilRef.current = Date.now() + LOAD_COOLDOWN_MS;
    } catch {
      clearPendingMessageListScroll();
      dispatch(
        updateMessageOptions({
          isMoreMessageLoading: false,
          has_more_newer: false,
        }),
      );
      newerEdgeReadyRef.current = true;
    } finally {
      inFlightRef.current = false;
    }
  }

  useEffect(() => {
    const el = document.getElementById("messageListDiv");
    if (!el) return;

    let ticking = false;

    const maybeRearm = () => {
      const distOld = distanceToOldest(el);
      if (distOld != null && distOld >= REARM_DISTANCE_PX) {
        olderEdgeReadyRef.current = true;
      }
      const distNew = distanceToNewest(el);
      if (distNew != null && distNew >= REARM_DISTANCE_PX) {
        newerEdgeReadyRef.current = true;
      }
      // Do NOT use scrollTop≈0 as "near tip": with flex-col-reverse Chrome
      // uses negative scrollTop in history, so scrollTop<=N is almost always true
      // and was re-arming / chaining loadNewer mid-history (teleport to tip).
    };

    const tryPrefetch = () => {
      if (!armedRef.current || inFlightRef.current) return;
      if (Date.now() < cooldownUntilRef.current) return;

      if (hasMoreOlderRef.current && olderEdgeReadyRef.current) {
        const dist = distanceToOldest(el);
        if (dist != null && dist < PREFETCH_DISTANCE_PX) {
          void loadOlder();
          return;
        }
      }

      if (hasMoreNewerRef.current && newerEdgeReadyRef.current) {
        const dist = distanceToNewest(el);
        // Only distance to newest bubble — never scrollTop alone (col-reverse).
        if (dist != null && dist < PREFETCH_DISTANCE_PX) {
          void loadNewer();
        }
      }
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        maybeRearm();
        tryPrefetch();
      });
    };

    const onWheel = (e: WheelEvent) => {
      if (!armedRef.current) return;
      // Scroll up → older
      if (e.deltaY < 0 && hasMoreOlderRef.current) {
        const dist = distanceToOldest(el);
        if (dist != null && dist < REARM_DISTANCE_PX) {
          olderEdgeReadyRef.current = true;
          tryPrefetch();
        }
        return;
      }
      // Scroll down → newer (pin / jump window)
      if (e.deltaY > 0 && hasMoreNewerRef.current) {
        const dist = distanceToNewest(el);
        if (dist != null && dist < REARM_DISTANCE_PX) {
          newerEdgeReadyRef.current = true;
          tryPrefetch();
        }
      }
    };

    let touchStartY = 0;
    const onTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!armedRef.current) return;
      const y = e.touches[0]?.clientY ?? 0;
      const dy = y - touchStartY;
      // Finger down → content moves up → older
      if (dy > 28 && hasMoreOlderRef.current) {
        const dist = distanceToOldest(el);
        if (dist != null && dist < REARM_DISTANCE_PX) {
          olderEdgeReadyRef.current = true;
          tryPrefetch();
        }
      }
      // Finger up → content moves down → newer
      if (dy < -28 && hasMoreNewerRef.current) {
        const dist = distanceToNewest(el);
        if (dist != null && dist < REARM_DISTANCE_PX) {
          newerEdgeReadyRef.current = true;
          tryPrefetch();
        }
      }
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("wheel", onWheel, { passive: true });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
    };
  }, [currentConversation.chat_id, dispatch]);

  useEffect(() => {
    if (!messageListDetails.messageListAtTop) return;
    dispatch(updateMessageOptions({ messageListAtTop: false }));
    if (!armedRef.current || !olderEdgeReadyRef.current) return;
    void loadOlder();
  }, [messageListDetails.messageListAtTop, dispatch]);

  // After pin jump into window mode, try one newer pack ONLY if visually near tip.
  // Distance-only: flex-col-reverse makes scrollTop<=N true deep in history.
  useEffect(() => {
    if (messageListDetails.historyMode !== "window") return;
    if (!messageListDetails.has_more_newer) return;
    const el = document.getElementById("messageListDiv");
    if (!el) return;
    const t = window.setTimeout(() => {
      if (!armedRef.current || !hasMoreNewerRef.current) return;
      const dist = distanceToNewest(el);
      if (dist != null && dist < PREFETCH_DISTANCE_PX) {
        newerEdgeReadyRef.current = true;
        void loadNewer();
      }
    }, 500);
    return () => window.clearTimeout(t);
  }, [
    messageListDetails.historyMode,
    messageListDetails.has_more_newer,
    currentConversation.chat_id,
  ]);

  return null;
}
